<template>
  <Teleport to="body">
    <div v-if="open" class="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-8">
      <div class="absolute inset-0 bg-black/50" @click="$emit('close')" />
      <div
        ref="panel"
        role="dialog"
        aria-modal="true"
        :aria-labelledby="labelledBy"
        tabindex="-1"
        class="relative w-full bg-surface border border-border shadow-xl outline-none"
        :class="panelClass"
        @keydown.esc.stop.prevent="$emit('close')"
        @keydown.tab="trapTab"
      >
        <slot />
      </div>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
// Scrim, role, Esc, Tab trap and focus restore for every modal. Key handling
// sits on the panel, not `document`: a dialog opened from inside another
// (PhotoStorageModal → ConfirmDialog) teleports to <body> as a sibling, so
// the focused panel is the only one that hears its keys.
//
// Focus goes to the first `[autofocus]` inside the panel, else the panel.
// `autofocus` is used purely as a marker — browsers ignore it on nodes
// inserted after load, so it is focused by hand.
//
// On close, focus goes back to the opener only when it was focused from the
// keyboard. A clicked button would otherwise keep focus and Space would press
// it again (reopening Music or Storage instead of playing the video); focus
// goes instead to the dialog the opener sits in, if any, else the page.
import { nextTick, onBeforeUnmount, ref, watch } from 'vue'

const props = defineProps<{
  open: boolean
  /** id of the heading that names the dialog */
  labelledBy?: string
  /** width, radius, padding and overflow per dialog */
  panelClass?: string
}>()

defineEmits<{ close: [] }>()

const panel = ref<HTMLElement | null>(null)
let previouslyFocused: HTMLElement | null = null

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'

function focusables(): HTMLElement[] {
  return panel.value ? Array.from(panel.value.querySelectorAll<HTMLElement>(FOCUSABLE)) : []
}

function trapTab(e: KeyboardEvent) {
  const items = focusables()
  if (!items.length) {
    e.preventDefault()
    return
  }
  const first = items[0]
  const last = items[items.length - 1]
  const active = document.activeElement
  if (e.shiftKey && (active === first || active === panel.value)) {
    e.preventDefault()
    last.focus()
  } else if (!e.shiftKey && active === last) {
    e.preventDefault()
    first.focus()
  }
}

function restoreFocus() {
  const el = previouslyFocused
  previouslyFocused = null
  if (el && document.contains(el)) el.focus()
}

watch(
  () => props.open,
  async (open) => {
    if (!open) {
      restoreFocus()
      return
    }
    const opener = document.activeElement as HTMLElement | null
    previouslyFocused = opener?.matches(':focus-visible')
      ? opener
      : opener?.closest<HTMLElement>('[role=dialog]') ?? null
    await nextTick()
    const target = panel.value?.querySelector<HTMLElement>('[autofocus]') ?? panel.value
    target?.focus()
  },
  { immediate: true },
)

onBeforeUnmount(restoreFocus)
</script>
